<template>
  <EChartCard
    :title="$t('inspectionProgress')"
    :buildOption="buildOption"
    :height="420"
    :refreshKey="bucketUnit"
  >
    <template #actions>
      <v-btn-toggle v-model="bucketOverride"
                    density="compact"
                    variant="outlined"
                    divided
                    class="bucket-toggle">
        <v-btn v-for="unit in UNITS" :key="unit" :value="unit" size="x-small" class="text-none">
          {{ $t(unit) }}
        </v-btn>
      </v-btn-toggle>
    </template>
  </EChartCard>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import EChartCard from '@/components/charts/base/EChartCard.vue';
import { getCategorical, statusColors } from '@/utils/vizPalette';
import { axisColor } from '@/utils/chartTheme';
import { bucketUnitForSpan, bucketLabel } from '@/utils/dateAxis';

const store = useStore();
const { t } = useI18n();

const UNITS = ['day', 'week', 'month'];

// Auto-picked from the data span unless the user overrides it.
const autoUnit = ref('month');
const bucketOverride = ref(null);
const bucketUnit = computed(() => bucketOverride.value || autoUnit.value);

const resolveAutoUnit = async (conn) => {
  // Every branch is cast to TIMESTAMP: CURRENT_TIMESTAMP is TIMESTAMPTZ, which
  // would make the union timestamptz and leave date_diff with no match.
  const result = await conn.query(`
    SELECT date_diff('day', MIN(d), MAX(d)) AS spanDays FROM (
      SELECT CAST(MIN(createdDate) AS TIMESTAMP) AS d FROM filteredAssets
      UNION ALL SELECT CAST(MAX(nextInspectionDate) AS TIMESTAMP) FROM filteredItems
      UNION ALL SELECT CAST(CURRENT_TIMESTAMP AS TIMESTAMP)
    )
  `);
  const row = result.toArray()[0]?.toJSON();
  autoUnit.value = bucketUnitForSpan(Number(row?.spanDays) || 0);
};

// Three cumulative curves over one calendar axis:
//   Total Scope  unique assets that exist by this bucket
//   Planned      inspection records scheduled up to this bucket
//   Inspected    inspection records completed up to this bucket
// GENERATE_SERIES gap-fills the calendar and SUM() OVER does the running total
// in SQL. Scope and Inspected stop at today; Planned continues into the future.
const buildQuery = (unit) => `
  WITH ev AS (
    SELECT CAST(createdDate AS DATE) AS d, 1 AS s, 0 AS p, 0 AS i
    FROM filteredAssets WHERE createdDate IS NOT NULL
    UNION ALL
    SELECT nextInspectionDate, 0, 1, 0 FROM filteredItems WHERE nextInspectionDate IS NOT NULL
    UNION ALL
    SELECT effectiveInspectionDate, 0, 0, 1 FROM filteredItems WHERE effectiveInspectionDate IS NOT NULL
  ),
  b AS (
    SELECT DATE_TRUNC('${unit}', d) AS bucket, SUM(s) AS s, SUM(p) AS p, SUM(i) AS i
    FROM ev GROUP BY 1
  ),
  cal AS (
    SELECT CAST(gs AS DATE) AS bucket
    FROM generate_series(
      (SELECT CAST(MIN(bucket) AS TIMESTAMP) FROM b),
      (SELECT CAST(GREATEST(MAX(bucket), DATE_TRUNC('${unit}', CURRENT_DATE)) AS TIMESTAMP) FROM b),
      INTERVAL 1 ${unit}
    ) t(gs)
  )
  SELECT
    strftime(cal.bucket, '%Y-%m-%d') AS bucketKey,
    CASE WHEN cal.bucket > CURRENT_DATE THEN NULL
         ELSE CAST(SUM(COALESCE(b.s, 0)) OVER (ORDER BY cal.bucket) AS BIGINT) END AS totalScope,
    CAST(SUM(COALESCE(b.p, 0)) OVER (ORDER BY cal.bucket) AS BIGINT) AS plannedCount,
    CASE WHEN cal.bucket > CURRENT_DATE THEN NULL
         ELSE CAST(SUM(COALESCE(b.i, 0)) OVER (ORDER BY cal.bucket) AS BIGINT) END AS inspectedCount
  FROM cal
  LEFT JOIN b USING (bucket)
  ORDER BY cal.bucket
`;

const buildOption = async () => {
  const conn = store.getters.getDBConnection;
  if (!conn) return null;

  if (!bucketOverride.value) await resolveAutoUnit(conn);

  const unit = bucketUnit.value;
  const result = await conn.query(buildQuery(unit));
  const rows = result.toArray().map((row) => {
    const json = row.toJSON();
    const num = (value) => (value === null || value === undefined ? null : Number(value));
    return {
      key: json.bucketKey,
      scope: num(json.totalScope),
      planned: num(json.plannedCount),
      inspected: num(json.inspectedCount),
    };
  });
  if (rows.length === 0) return null;

  const fontColor = store.getters.chartFontColor;
  const gridColor = axisColor(store.state.theme);
  const palette = getCategorical(store.state.theme);
  const todayKey = new Date().toISOString().slice(0, 10);
  // Index of the first bucket after today - where the "planned ahead" shading
  // starts.
  const futureIndex = rows.findIndex((row) => row.key > todayKey);

  const series = [
    { name: t('totalScope'), key: 'scope', color: statusColors.neutral },
    { name: t('planned'), key: 'planned', color: palette[0] },
    { name: t('inspected'), key: 'inspected', color: statusColors.good },
  ].map((spec, index) => ({
    name: spec.name,
    type: 'line',
    smooth: true,
    showSymbol: false,
    connectNulls: false,
    lineStyle: { width: 2, color: spec.color },
    itemStyle: { color: spec.color },
    areaStyle: { color: spec.color, opacity: 0.1 },
    data: rows.map((row) => row[spec.key]),
    // Hang the today marker off one series only.
    markLine: index === 1 && futureIndex > 0
      ? {
        silent: true,
        symbol: 'none',
        lineStyle: { color: fontColor, opacity: 0.5, type: 'dashed' },
        label: { formatter: t('today'), color: fontColor, fontSize: 11 },
        data: [{ xAxis: futureIndex - 1 }],
      }
      : undefined,
    markArea: index === 1 && futureIndex > 0
      ? {
        silent: true,
        itemStyle: { color: fontColor, opacity: 0.04 },
        data: [[{ xAxis: futureIndex - 1 }, { xAxis: rows.length - 1 }]],
      }
      : undefined,
  }));

  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'line' } },
    legend: {
      top: 0,
      left: 'center',
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: fontColor },
    },
    grid: { left: 8, right: 16, top: 44, bottom: 4, containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: rows.map((row) => bucketLabel(row.key, unit)),
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: { color: fontColor, hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: fontColor },
      splitLine: { lineStyle: { color: gridColor } },
    },
    series,
  };
};

// v-btn-toggle sets undefined when the active button is clicked again.
watch(bucketOverride, (value) => {
  if (value === undefined) bucketOverride.value = null;
});
</script>

<style scoped>
.bucket-toggle {
  height: 26px;
}
</style>
